// اللَّهُ لَا إِلَٰهَ إِلَّا هُوَ الْحَيُّ الْقَيُّومُ ۚ لَا تَأْخُذُهُ سِنَةٌ وَلَا نَوْمٌ ۚ
// لَهُ مَا فِي السَّمَاوَاتِ وَمَا فِي الْأَرْضِ ۗ مَنْ ذَا الَّذِي يَشْفَعُ عِنْدَهُ إِلَّا بِإِذْنِهِ ۚ
// يَعْلَمُ مَا بَيْنَ أَيْدِيهِمْ وَمَا خَلْفَهُمْ ۖ وَلَا يُحِيطُونَ بِشَيْءٍ مِنْ عِلْمِهِ إِلَّا بِمَا شَاءَ ۚ
// وَسِعَ كُرْسِيُّهُ السَّمَاوَاتِ وَالْأَرْضَ ۖ وَلَا يَئُودُهُ حِفْظُهُمَا ۚ وَهُوَ الْعَلِيُّ الْعَظِيمُ
// (Bakara 255)

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'lucide-react';
import { EcosystemSidePanel } from '@/components/EcosystemSidePanel';
import { useBrand } from '@/hooks/useBrand';
import { LanguageSelector } from '@/components/LanguageSelector';

interface AuthShellProps {
  title: string;
  subtitle?: ReactNode;
  error?: string | null;
  children: ReactNode;
  footer?: ReactNode;
  width?: 'sm' | 'md' | 'lg';
  hideSidePanel?: boolean;
}

const widthClass: Record<NonNullable<AuthShellProps['width']>, string> = {
  sm: 'max-w-[400px]',
  md: 'max-w-[420px]',
  lg: 'max-w-[480px]',
};

export function AuthShell({
  title,
  subtitle,
  error,
  children,
  footer,
  width = 'md',
  hideSidePanel = false,
}: AuthShellProps) {
  const { t } = useTranslation();
  const brand = useBrand();

  return (
    <div className="min-h-screen flex" style={{ fontFamily: "'Inter', sans-serif" }}>
      {!hideSidePanel && <EcosystemSidePanel />}

      <div className="flex-1 flex flex-col items-center bg-background px-6 py-10 relative overflow-y-auto">
        {/* DİL SEÇİCİ — giriş yapmadan dil değiştirilebilsin (kullanıcı kararı 2026-08-30).
            Konum sağ üst köşe: kullanıcının dil kontrolünü aradığı yer orası. Footer denendi ve
            elendi (kaydırmadan görünmüyor), sol marka paneli de elendi (dar ekranda gizleniyor,
            dilini değiştirmek isteyen kullanıcı telefonda hiç bulamazdı).
            Görsel ağırlık bilerek düşük — bayrak + kod, çerçevesiz, soluk metin: giriş butonunun
            önüne geçmesin, ama arayan gözle bulsun. Seçim localStorage'a yazıldığı için giriş
            sonrası uygulamada da geçerli kalır. */}
        <div className="absolute top-4 right-4 z-20">
          <LanguageSelector gorunum="sade" />
        </div>

        <div
          className="absolute inset-0 pointer-events-none opacity-40"
          style={{ backgroundImage: 'radial-gradient(circle at 1px 1px,#e2e8f0 1px,transparent 0)', backgroundSize: '24px 24px' }}
        />
        {/* Marka tonunda yumuşak glow — sol panelle renk uyumu + derinlik */}
        <div
          className="absolute -top-24 -right-24 w-96 h-96 rounded-full pointer-events-none elpo-float"
          style={{ background: `radial-gradient(circle, ${brand.primaryColor}22, transparent 70%)` }}
        />
        <div
          className="absolute -bottom-28 -left-20 w-96 h-96 rounded-full pointer-events-none elpo-float"
          style={{ background: `radial-gradient(circle, color-mix(in srgb, ${brand.primaryColor} 18%, transparent), transparent 70%)`, animationDelay: '-4s' }}
        />
        <div className={`relative z-10 w-full ${widthClass[width]}`}>
          {/* Mobile brand bloku */}
          <div className="flex items-center justify-center mb-6 lg:hidden flex-col gap-1">
            {/* Dar ekranda sol tanıtım paneli gizlenir ve marka bloğu BURADA çizilir. White-label
                markası kendi logosunu vermediğinde eskiden jenerik bir yıldırım ikonu çıkıyordu —
                kullanıcı bunu "ELPO logosu kayboldu, yerine saçma bir işaret geldi" olarak bildirdi
                (2026-08-18). Varsayılan artık gerçek ELPO logosu; yıldırım hiç kullanılmıyor. */}
            <img
              src={brand.logo ?? '/elpo-logo.png'}
              alt={brand.displayName}
              className="h-10 mb-1 object-contain"
            />
            <span
              className="text-2xl font-bold bg-clip-text text-transparent leading-normal pb-0.5"
              style={{ backgroundImage: `linear-gradient(90deg, ${brand.primaryColor}, color-mix(in srgb, ${brand.primaryColor} 55%, #0c1b3d))` }}
            >
              {brand.displayName}
            </span>
            {brand.tagline && <p className="text-[11px] text-muted-foreground">{brand.tagline}</p>}
          </div>

          <div className="mb-6 elpo-reveal" style={{ ['--d' as string]: '80ms' }}>
            <h1 className="text-2xl font-bold text-foreground">{title}</h1>
            {subtitle && <p className="text-muted-foreground text-sm mt-1">{subtitle}</p>}
          </div>

          {error && (
            <div className="flex items-center gap-2 px-4 py-3 mb-4 bg-destructive/10 border border-destructive/30 rounded-xl">
              <AlertCircle className="w-4 h-4 text-destructive shrink-0" />
              <span className="text-[13px] text-destructive">{error}</span>
            </div>
          )}

          <div className="elpo-reveal" style={{ ['--d' as string]: '170ms' }}>
            {children}
          </div>

          {footer && <div className="mt-6 elpo-reveal" style={{ ['--d' as string]: '250ms' }}>{footer}</div>}

          {/* SABİT tek satır — eski login tasarımı DEĞİŞMEZ, sadece bu satır eklenir. docs/LOGIN_STANDARD.md */}
          <div className="mt-8 flex items-center justify-center">
            <p className="text-[12px] text-muted-foreground flex items-center gap-1.5 flex-wrap justify-center">
              <span className="font-semibold text-foreground">{brand.displayName}</span>
              <a href="https://www.elpo.com.tr" target="_blank" rel="noreferrer" className="inline-flex items-center">
                <img src="/elpo-logo.png" alt="Elpo" className="h-4 object-contain" />
              </a>
              <span>{t('login.istirakidir')}</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
